<template>
	<div
		ref="listRef"
		class="relative flex items-center w-full md:w-[calc(20%-20px)] min-h-[56px] pl-0 sm:pl-4 md:pl-6 pr-0 sm:pr-4 pb-2 md:pb-0 md:mt-0 mt-3 border-b-[1px] border-gray-light md:border-0"
	>
		<div @click="showList = !showList" class="flex-col cursor-pointer w-[calc(100%-30px)] pr-2">
			<span class="pb-1 text-dj-gray-dark text-sm font-medium">
				{{ $t("companies.searchbar.industry.title") }}
			</span>
			<input
				v-if="canType || selectedIndustries.length == 0"
				ref="inputRef"
				v-model="inputVmodel"
				class="w-full outline-none border-none bg-transparent max-h-6"
				type="text"
				:placeholder="placeholder"
			/>
			<span
				v-else
				@click="canType = true"
				class="block overflow-hidden overflow-ellipsis whitespace-nowrap w-[calc(100%-6px)] cursor-default"
			>
				{{
					selectedIndustries
						.map((industry) => industry.name)
						.map((i) => i[locale])
						.join(", ")
				}}
			</span>
		</div>

		<DropdownButton v-model="showList" class="mt-2" />

		<div
			v-show="showList"
			class="absolute p-5 left-0 md:-left-8 top-[60px] md:top-[85px] w-[400px] max-w-[100%] md:max-w-[100vw] max-h-[350px] bg-white border border-dj-gray-light rounded-big flex flex-col z-30"
		>
			<h3 class="font-medium mb-[10px]">{{ $t("companies.searchbar.location.results") }}</h3>
			<div class="flex flex-col gap-y-[10px] overflow-auto">
				<template v-if="inputVmodel.length != 0">
					<BaseCheckbox
						v-model="industry.value"
						@change="onChange($event, industry)"
						v-for="(industry, index) in filteredIndustries"
						:key="index"
					>
						{{ industry.name[locale] }}
					</BaseCheckbox>
				</template>
				<template v-else>
					<BaseCheckbox
						v-for="(industry, index) in industriesWithValue"
						:key="index"
						v-model="industry.value"
						@change="onChange($event, industry)"
					>
						{{ industry.name[locale] }}
					</BaseCheckbox>
				</template>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, type PropType } from "vue";
import { onClickOutside, useFocus } from "@vueuse/core";
import { useI18n } from "vue-i18n";
import axios from "axios";

import { sortArrayByObjectByLocale } from "../../../composables/arrayHelpers";

export default defineComponent({
	name: "SearchbarCompaniesIndustry",
	props: {
		modelValue: {
			type: Array as PropType<string[]>,
			required: true,
		},
		filterList: {
			type: Array as PropType<string[]>,
			default: [],
		},
		placeholder: {
			type: String,
			default: "",
		},
	},
	emits: ["update:modelValue"],
	async setup(props, { emit }) {
		const { locale } = useI18n();

		const inputRef = ref(null);
		const listRef = ref(null);

		const inputVmodel = ref("");

		const industryData = ref();
		const industriesWithValue = ref([]);
		const filteredIndustries = ref([]);
		const selectedIndustries = ref<string[]>([]);

		const showList = ref(false);
		const canType = ref(false);

		if (props.filterList.length > 0) {
			industryData.value = props.filterList;
			setDataArrays();
		} else {
			// Fallback API
			await axios(globalConfig.API + "/tags?type[]=industry&limit=-1").then((response) => {
				industryData.value = response.data.data;
				setDataArrays();
			});
		}

		function transformArray() {
			return sortArrayByObjectByLocale(industryData.value, "name", locale.value).value.map((industry) => {
				return {
					...industry,
					value: false,
				};
			});
		}

		function setDataArrays() {
			industriesWithValue.value = transformArray();
			filteredIndustries.value = industriesWithValue.value;
		}

		watch(inputVmodel, () => {
			if (inputVmodel.value.length) {
				const pattern = new RegExp(inputVmodel.value, "gi");
				filteredIndustries.value = industriesWithValue.value.filter((elem) =>
					elem.name[locale.value].match(pattern)
				);
				showList.value = true;
			} else {
				filteredIndustries.value = industriesWithValue.value;
			}
		});

		const { focused: inputFocus } = useFocus(inputRef);

		onClickOutside(listRef, () => {
			showList.value = false;
			inputVmodel.value = "";
		});

		onClickOutside(inputRef, () => {
			canType.value = false;
		});

		onMounted(() => {
			// get locations ID from url & set selectedlocations
			const query = useRoute().query;
			if (query.industry) {
				const queryIndustry = query.industry.toString().split(",");
				queryIndustry.forEach((ql) => {
					const num = Number.parseInt(ql);

					const targetIndex = industriesWithValue.value.findIndex((pred) => pred.id == num);
					if (targetIndex > -1) {
						industriesWithValue.value[targetIndex].value = true;
						selectedIndustries.value.push(industriesWithValue.value[targetIndex]);
					}
				});
				emit("update:modelValue", selectedIndustries.value);
			}
		});

		function onChange(event, settlement: string) {
			event
				? selectedIndustries.value.push(settlement)
				: selectedIndustries.value.splice(selectedIndustries.value.indexOf(settlement), 1);
			emit("update:modelValue", selectedIndustries.value);
		}

		return {
			inputRef,
			inputVmodel,
			inputFocus,
			listRef,
			showList,
			filteredIndustries,
			industriesWithValue,
			onChange,
			selectedIndustries,
			canType,
			locale,
		};
	},
});
</script>
